
import omitBy from 'lodash/omitBy'
import isNil from 'lodash/isNil'
import HotelsListItem from '@/components/HotelsListItem.vue'
import HotelsMapCard from '@/components/HotelsMapCard.vue'
import HotelsMap from '@/components/HotelsMap.vue'
import HotelsFilters from '@/components/HotelsFilters.vue'
import KayakAdInline from '@/components/KayakAdInline.vue'
import analytics from '@/helpers/analytics'

export default {
  components: {
    HotelsListItem,
    HotelsMapCard,
    HotelsMap,
    HotelsFilters,
    KayakAdInline
  },
  props: {
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      topInlineAds: 3,
      altarnateEach: 4,
      viewMode: 'list',
      mapEnabledTracked: false,
      mapDisabledTracked: false,
      selectedHotelId: null,
      mapFiltersOpen: false,
      mapBounds: null
    }
  },
  computed: {
    activeFilterCount () {
      const checkboxes = this.$store.state.searchHotelsCheckboxes
      if (!checkboxes || typeof checkboxes !== 'object') return 0
      let unchecked = 0
      Object.values(checkboxes).forEach(group => {
        if (group && group.options) {
          Object.values(group.options).forEach(opt => {
            if (opt && !opt.checked) unchecked++
          })
        }
      })
      return unchecked
    },
    mapVisibleResults () {
      if (!this.mapBounds) return this.sortedHotelResults
      const { north, south, east, west } = this.mapBounds
      return this.sortedHotelResults.filter(h => {
        // Always keep the selected hotel visible in the list
        if (this.selectedHotelId && String(h.id) === String(this.selectedHotelId)) return true
        if (!h.location || !h.location.lat || !h.location.lon) return true
        return h.location.lat >= south && h.location.lat <= north &&
               h.location.lon >= west && h.location.lon <= east
      })
    },
    firstDeepLink () {
      if (!this.$store.state.deeplinks.length) {
        return ''
      }
      const firstLink = this.$store.state.deeplinks.filter(item => item.provider.toLowerCase() !== 'travelmedia')

      return firstLink[0] ? firstLink[0].redirect : ''
    },
    inlineAds () {
      const ads = this.$store.state.inlineAds
      if (!ads || !ads.length) return [null, null, null]
      return this.parseInlineAds(ads)
    },
    shouldShowHeading () {
      return this.hasItems || this.hasResultItems
    },
    parsedFilteredHotelsResults () {
      return this.$store.getters.parsedFilteredHotelsResults
    },
    sortedHotelResults () {
      const list = [...this.parsedFilteredHotelsResults]
      const sort = this.$store.state.hotelSort
      const nights = Math.max(1, this.$store.state.hotelStayNights || 1)
      const local = this.$store.state.localCurrency
      const priceMode = this.$store.state.hotelPriceDisplayMode
      const priceOf = (r) => {
        const raw = local ? r.minPriceLocal : r.minPrice
        if (priceMode === 'nightly') {
          return raw / nights
        }
        return raw
      }
      const guestScore = (r) => {
        const g = parseFloat(r.guestRating)
        if (!Number.isNaN(g) && g > 0) return g
        return parseInt(r.starRating, 10) || 0
      }
      if (sort === 'price_asc') {
        list.sort((a, b) => priceOf(a) - priceOf(b))
      } else if (sort === 'price_desc') {
        list.sort((a, b) => priceOf(b) - priceOf(a))
      } else if (sort === 'rating') {
        list.sort((a, b) => guestScore(b) - guestScore(a))
      }
      return list
    },
    resultsHeading () {
      const count = this.parsedFilteredHotelsResults.length
      const q = this.$route.query || {}
      const raw = (q.destCity || q.destinationText || q.cityName || this.$t('hotels.site.object')).toString()
      let city = raw
      try {
        city = decodeURIComponent(raw.replace(/\+/g, ' '))
      } catch (e) {
        city = raw.replace(/\+/g, ' ')
      }
      return this.$t('hotels.results.heading', { city, count })
    },
    items () {
      const { sortedHotelResults, inlineAds, topInlineAds, altarnateEach } = this

      const resultsCopy = [...sortedHotelResults]
      const items = []

      for (let i = 0; i < Math.min(resultsCopy.length, inlineAds.length); i++) {
        items.push(inlineAds[i], resultsCopy[i])
      }
      if (inlineAds.length > resultsCopy.length) {
        items.push(...inlineAds.slice(resultsCopy.length))
      }

      const topAds = Math.min(topInlineAds, inlineAds.length)
      let adsIndex = 0

      for (let i = inlineAds.length + altarnateEach - 2; i < resultsCopy.length; i += altarnateEach) {
        resultsCopy.splice(i, 0, inlineAds[adsIndex])
        adsIndex = adsIndex === topAds - 1 ? 0 : adsIndex + 1
      }
      items.push(...resultsCopy.slice(inlineAds.length))

      return Array.isArray(items) ? omitBy(items, isNil) : []
    },
    hasResultItems () {
      return this.$store.state.searchResults.length > 0
    },
    hasItems () {
      return this.items && Object.values(this.items).length
    }
  },
  watch: {
    hasResultItems (newVal) {
      if (newVal) {
        const firstResult = this.$store.state.searchResults[0]
        this.fireAdaraEvent(firstResult)
      }
    }
  },
  methods: {
    setViewMode (mode) {
      this.viewMode = mode
      this.$emit('viewChange', mode)
      if (mode === 'map' && !this.mapEnabledTracked) {
        this.mapEnabledTracked = true
        analytics.track('hotels-map-enabled', this)
      } else if (mode === 'list' && !this.mapDisabledTracked) {
        this.mapDisabledTracked = true
        analytics.track('hotels-map-disabled', this)
      }
    },
    selectHotel (hotelId) {
      this.selectedHotelId = hotelId
    },
    selectHotelFromMap (hotelId) {
      this.selectedHotelId = hotelId
      this.scrollToCard(hotelId)
    },
    showOnMap (hotelId) {
      this.selectedHotelId = hotelId
      this.$nextTick(() => {
        const map = this.$refs.hotelsMap
        if (map && map.panTo) {
          map.panTo(hotelId)
        }
      })
      this.scrollToCard(hotelId)
    },
    scrollToCard (hotelId) {
      const tryScroll = () => {
        const container = this.$refs.mapCardList
        if (!container) return false
        const el = container.querySelector('[data-hotel-id="' + hotelId + '"]')
        if (el) {
          const offset = el.offsetTop - container.offsetTop
          container.scrollTo({ top: Math.max(0, offset - 10), behavior: 'smooth' })
          return true
        }
        container.scrollTop = 0
        return false
      }
      this.$nextTick(() => {
        tryScroll()
        setTimeout(tryScroll, 500)
        setTimeout(tryScroll, 1000)
      })
    },
    onCardHover (hotelId) {
      const map = this.$refs.hotelsMap
      if (map && map.highlightPin) map.highlightPin(hotelId)
    },
    onCardUnhover (hotelId) {
      const map = this.$refs.hotelsMap
      if (map && map.unhighlightPin) map.unhighlightPin(hotelId)
    },
    fireAdaraEvent (item) {
      if (Object.keys(item).length === 0) return

      const adaraParams = {
      }
      try {
        window.adara('send', {
          pageType: 'Results Item',
          siteLanguage: this.$store.state.language,
          siteCountry: this.$store.state.country,
          ...adaraParams
        })
      } catch (e) {
        console.info('Error firing Adara event')
      }
    },
    parseInlineAds (ads) {
      return ads.map((ad, index) => ({ ...ad, id: `kayak-${index}`, component: 'KayakAdInline' }))
    },
    handleNoResultsClick () {
      analytics.track('meta-hotel-no-results-click', this)
    },
    openCompareModal (item) {
      this.$emit('openCompareModal', { hotel: item })
    },
    compareAll (data) {
      this.$emit('compareAll', data)
    }
  }
}
